import { BN } from '@project-serum/anchor'
import { AccountMeta, PublicKey, Signer, TransactionInstruction } from '@solana/web3.js'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Mint, Account, TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { struct, u8 } from '@solana/buffer-layout'
import { u64 } from '@solana/buffer-layout-utils'

export enum TokenInstruction {
  InitializeMint = 0,
  InitializeAccount = 1,
  InitializeMultisig = 2,
  Transfer = 3,
  Approve = 4,
  Revoke = 5,
  SetAuthority = 6,
  MintTo = 7,
  Burn = 8,
  CloseAccount = 9,
  FreezeAccount = 10,
  ThawAccount = 11,
  TransferChecked = 12,
  ApproveChecked = 13,
  MintToChecked = 14,
  BurnChecked = 15,
  InitializeAccount2 = 16,
  SyncNative = 17,
  InitializeAccount3 = 18,
  InitializeMultisig2 = 19,
  InitializeMint2 = 20,
  GetAccountDataSize = 21,
  InitializeImmutableOwner = 22,
  AmountToUiAmount = 23,
  UiAmountToAmount = 24,
  InitializeMintCloseAuthority = 25,
  TransferFeeExtension = 26,
  ConfidentialTransferExtension = 27,
  DefaultAccountStateExtension = 28,
  Reallocate = 29,
  MemoTransferExtension = 30,
  CreateNativeMint = 31,
  InitializeNonTransferableMint = 32,
  InterestBearingMintExtension = 33,
}

export interface ApproveInstructionData {
  instruction: number
  amount: bigint
}

/** TODO: docs */
export const approveInstructionData = struct<ApproveInstructionData>([
  u8('instruction'),
  u64('amount'),
])

export interface RevokeInstructionData {
  instruction: TokenInstruction.Revoke
}

export function createApproveInstruction(
  account: PublicKey,
  delegate: PublicKey,
  owner: PublicKey,
  amount: number | bigint,
  multiSigners: Signer[] = [],
  programId = TOKEN_PROGRAM_ID
): TransactionInstruction {
  const keys = addSigners(
    [
      { pubkey: account, isSigner: false, isWritable: true },
      { pubkey: delegate, isSigner: false, isWritable: false },
    ],
    owner,
    multiSigners
  )

  const data = Buffer.alloc(approveInstructionData.span)
  approveInstructionData.encode(
    {
      instruction: 4,
      amount: BigInt(amount),
    },
    data
  )

  return new TransactionInstruction({ keys, programId, data })
}

export const revokeInstructionData = struct<RevokeInstructionData>([u8('instruction')])

export function createRevokeInstruction(
  account: PublicKey,
  owner: PublicKey,
  multiSigners: Signer[] = [],
  programId = TOKEN_PROGRAM_ID
): TransactionInstruction {
  const keys = addSigners(
    [{ pubkey: account, isSigner: false, isWritable: true }],
    owner,
    multiSigners
  )

  const data = Buffer.alloc(revokeInstructionData.span)
  revokeInstructionData.encode({ instruction: TokenInstruction.Revoke }, data)

  return new TransactionInstruction({ keys, programId, data })
}

export function addSigners(
  keys: AccountMeta[],
  ownerOrAuthority: PublicKey,
  multiSigners: Signer[]
): AccountMeta[] {
  if (multiSigners.length) {
    keys.push({ pubkey: ownerOrAuthority, isSigner: false, isWritable: false })
    for (const signer of multiSigners) {
      keys.push({ pubkey: signer.publicKey, isSigner: true, isWritable: false })
    }
  } else {
    keys.push({ pubkey: ownerOrAuthority, isSigner: true, isWritable: false })
  }
  return keys
}

export type Network =
  | 'http://127.0.0.1:8899'
  | 'https://api.devnet.solana.com'
  | 'https://api.mainnet-beta.solana.com'

export type PlatformSettings = {
  platformTreasury: PublicKey
  platformSigner: PublicKey
}

export type UI<T> = T & {
  key: PublicKey
}

export type EditionMetadata = {
  tokenOwner: PublicKey
  mint: PublicKey
  metadata: PublicKey
  primarySalePrice: BN
  maxSupply: number
  primarySaleTokenLimit: number
  numberOfSales?: number
  platformFeeBasisPoints: number
  creatorFeeBasisPoints: number
  platformCreateFeeLamports: BN
  platformPrimarySaleFeeBasisPoints: number
}

export type EditionMetadataInfo = {
  key: PublicKey
  mintInfo: Mint
  metadataInfo: DataV2
  creators: AccountBalance[]
  metadataJson: MetadataJson
  tokenAccount: Account
}

export type AccountBalance = {
  key: PublicKey
  lamports: number
}

export type DataV2 = {
  name: string
  symbol: string
  uri: string
  sellerFeeBasisPoints: number
  creators: Creator[]
  collection: Collection | null
  uses: Uses | null
}

export type Creator = {
  address: PublicKey
  verified: boolean
  share: number
}

export type Collection = {
  verified: boolean
  key: PublicKey
}

export type UseMethod = { burn: {} } | { multiple: {} } | { single: {} }

export type Uses = {
  useMethod: UseMethod
  remaining: number
  total: number
}

export type Attribute = {
  traitType: string | number
  value: string | number
}

export type CreatorJson = {
  address: string
  share: number
}

export type MetadataJson = {
  name: string
  symbol?: string
  description?: string
  sellerFeeBasisPoints: number
  image: string
  animationUrl: string
  externalUrl: string
  attributes: Attribute[]
  collection: {
    name: string
    family: string
  }
  properties: {
    files: {
      uri: string
      type: 'image/png' | 'video/mp4'
    }[]
    category: 'video' | 'image'
    creators?: CreatorJson[]
  }
}
